import { pick } from 'lodash/fp';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavigateFunction, useNavigate } from 'react-router-dom-v5-compat';

import LoadingModal from 'dashboard/components/LoadingModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import WorkflowActivateModal from 'dashboard/components/WorkflowActivateModal';
import WorkflowDeactivateModal from 'dashboard/components/WorkflowDeactivateModal';
import WorkflowEditor from 'dashboard/components/WorkflowEditor';
import WorkflowManualRunModal from 'dashboard/components/WorkflowManualRunModal';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import { TransactionType } from 'dashboard/models/transaction-type';
import {
  Workflow,
  WorkflowError,
  WorkflowErrorType,
  WorkflowSchema,
  WorkflowStatus,
} from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import {
  WorkflowStepInput,
  WorkflowVersion,
  WorkflowVersionAttributes,
  WorkflowVersionDeploymentStatus,
  WorkflowVersionStatus,
} from 'dashboard/models/workflow-version';
import { ManualRunResults } from 'dashboard/redux/reducers/workflow-runs';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import { ConfigQueryParam } from 'dashboard/routes/DashboardWorkflowsShowRoute/types';
import WorkflowConfigModal, {
  EditorNavItemLinkProps,
  ModalSectionView,
} from 'dashboard/views/DashboardWorkflowsShowView/WorkflowConfigModal';
import WorkflowEnvironmentsConfiguration from 'dashboard/views/DashboardWorkflowsShowView/WorkflowEnvironmentsConfiguration';
import WorkflowHeader from 'dashboard/views/DashboardWorkflowsShowView/WorkflowHeader';
import WorkflowPublishModal from 'dashboard/views/DashboardWorkflowsShowView/WorkflowPublishModal';
import WorkflowStatusBanner from 'dashboard/views/DashboardWorkflowsShowView/WorkflowStatusBanner';
import WorkflowVersionDiffModal from 'dashboard/views/DashboardWorkflowsShowView/WorkflowVersionDiffModal';
import useConfirmation from 'lib/hooks/useConfirmation';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { useKeydownSaveListener, useKeydownUndoListener } from 'lib/hooks/useKeyListener';
import usePermission from 'lib/hooks/usePermission';
import { usePreserveQueries } from 'lib/hooks/usePreserveQuery';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface DashboardWorkflowsShowViewProps {
  loading: boolean;
  configureEnvironmentsLoading: boolean;
  workflow: Workflow;
  workflowVersion: WorkflowVersion;
  publishedWorkflowVersion?: WorkflowVersion;
  workflowDeployment: WorkflowDeployment | null;
  workflowSchema: WorkflowSchema;
  transactionTypes: TransactionType[];
  environments: Environment[];
  manualRunResults?: ManualRunResults;
  organization: Organization;
  inputStep: WorkflowStepInput;
  canUndo: boolean;
  isDirty: boolean;
  editingWorkflowVersionId: string;
  validationErrors: WorkflowError[];
  isLoadingVersionList: boolean;
  versionsList: (WorkflowVersion | WorkflowBlock)[];
  configQueryParam: ConfigQueryParam;
  onConfigureEnvironments: (activeEnvironmentIds: string[], rolloutPercentage: number) => void;
  onUpdateWorkflow: (attributes: { name?: string; description?: string }) => Promise<void>;
  onClickSave: () => void;
  onClickRollout: (
    rolloutPercentage: number,
    description?: string,
    environmentIds?: string[],
  ) => void;
  onClickArchive: () => Promise<void>;
  loadWorkflowVersion: (workflowVersionId: string) => void;
  loadLatestPublishedVersion: () => void;
  pauseWorkflow: (workflow: Workflow) => void;
  createSimulatedWorkflowRun: (
    workflowId: Workflow['id'],
    workflowVersionId: WorkflowVersion['id'],
    navigate: NavigateFunction,
  ) => void;
  runWorkflow: (workflow: Workflow) => void;
  setWorkflowVersion: (workflowVersion: WorkflowVersion) => void;
  setInputStep: (inputStep: WorkflowStepInput) => void;
  undoWorkflow: () => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
  createBulkManualRuns: (objectIds: string[]) => Promise<void>;
  clearManualRunResults: () => void;
  isLoadingCreateBulkManualRuns: boolean;
  loadFilterFieldConfigs?: () => Promise<void>;
  currentEnvironmentType: Environment['type'];
  loadVersionList: () => Promise<void>;
  restoreDraftVersion: () => Promise<void>;
  handleDiscardDraft: () => Promise<void>;
  fetchPlatformObjectJsonSchema: (template?: TemplateFilter) => Promise<ObjectSchemaMap>;
}

const DashboardWorkflowsShowView = ({
  loading,
  configureEnvironmentsLoading,
  workflow,
  workflowVersion,
  publishedWorkflowVersion,
  workflowDeployment,
  workflowSchema,
  transactionTypes,
  environments,
  organization,
  inputStep,
  isLoadingVersionList,
  versionsList,
  configQueryParam,
  onConfigureEnvironments,
  onUpdateWorkflow,
  onClickSave,
  onClickRollout,
  onClickArchive,
  loadWorkflowVersion,
  loadLatestPublishedVersion,
  pauseWorkflow,
  runWorkflow,
  setWorkflowVersion,
  setInputStep,
  undoWorkflow,
  canUndo,
  validationErrors,
  isDirty,
  showMessage,
  createBulkManualRuns,
  clearManualRunResults,
  manualRunResults,
  isLoadingCreateBulkManualRuns,
  loadFilterFieldConfigs,
  createSimulatedWorkflowRun,
  currentEnvironmentType,
  loadVersionList,
  restoreDraftVersion,
  handleDiscardDraft,
  fetchPlatformObjectJsonSchema,
}: DashboardWorkflowsShowViewProps) => {
  const navigate = useNavigate();
  const isPersonerd = useIsPersonerd();
  const preserveQueries = usePreserveQueries();

  const {
    id: workflowId,
    attributes: { name: workflowName, description: workflowDescription, status: workflowStatus },
    relationships: {
      'latest-published-version': latestPublishedVersion,
      'latest-draft-version': latestDraftVersion,
      environments: { data: activeEnvironments },
    },
  } = workflow;
  const activeEnvironmentIds = activeEnvironments.map((env) => env.id);
  const {
    attributes: { configuration },
  } = (() => {
    if (workflowDeployment != null) {
      return workflowDeployment;
    } else {
      return {
        attributes: {
          configuration: {
            versions: [] as WorkflowDeployment['attributes']['configuration']['versions'],
          },
        },
      };
    }
  })();

  const {
    id: workflowVersionId,
    attributes: { status: workflowVersionStatus, 'steps-config': stepsConfig },
  } = workflowVersion;

  const isTriggerCase = inputStep.trigger?.type === ApiModel.TriggerCase;
  const basePathRoot = isTriggerCase ? 'case-actions' : 'workflows';
  const basePath = `/dashboard/${basePathRoot}/${workflowId}`;
  const fullPath = `${basePath}?version=${workflowVersionId}`;
  const handleCloseModal = useCallback(() => {
    navigate(fullPath, { replace: true });
  }, [navigate, fullPath]);

  const initialRolloutPercentage =
    configuration.versions.find(
      (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
    )?.percentage ?? 100;
  const treatmentVersionId = configuration.versions.find(
    (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
  )?.token;
  const controlVersionId = configuration.versions.find(
    (version) => version.label === WorkflowDeploymentConfigurationLabel.Control,
  )?.token;

  const [
    showArchiveWorkflowModal,
    { enable: openArchiveWorkflowModal, disable: closeArchiveWorkflowModal },
  ] = useToggle(false);

  const [
    isShowingWorkflowVersions,
    { enable: showWorkflowVersions, disable: hideWorkflowVersions },
  ] = useToggle(false);

  const closeWorkflowVersionView = () => {
    hideWorkflowVersions();
  };

  const [
    showPublishWorkflowModal,
    { enable: openPublishWorkflowModal, disable: closePublishWorkflowModal },
  ] = useToggle(false);

  const [
    showWorkflowVersionDiffModal,
    { enable: openWorkflowVersionDiffModal, disable: closeWorkflowVersionDiffModal },
  ] = useToggle(false);

  const [
    showActivateWorkflowModal,
    { enable: openActivateWorkflowModal, disable: closeActivateWorkflowModal },
  ] = useToggle(false);

  const [
    showDeactivateWorkflowModal,
    { enable: openDeactivateWorkflowModal, disable: closeDeactivateWorkflowModal },
  ] = useToggle(false);

  const [visibleValidationErrors, { enable: showValidationErrors, disable: hideValidationErrors }] =
    useToggle(false);
  const [focusedValidationErrorIndex, setFocusedValidationErrorIndex] = useState<number | null>(
    null,
  );
  const versionDescription =
    workflowVersion.attributes.status === WorkflowVersionStatus.Draft
      ? workflowVersion.attributes.description || ''
      : '';

  const [
    showManualTriggerModal,
    { enable: openManualTriggerModal, disable: closeManualTriggerModal },
  ] = useToggle(false);

  const handleSaveWorkflowVersion = useCallback(() => {
    const validationErrorsOnSave = validationErrors.filter(
      ({ type }) => type === WorkflowErrorType.Save,
    );
    if (validationErrorsOnSave.length !== 0) {
      showMessage('Please fix errors before saving', MessageType.Notice);
      showValidationErrors();
    } else {
      onClickSave();
    }
  }, [validationErrors, showMessage, showValidationErrors, onClickSave]);

  const handlePublishWorkflowVersion = useCallback(() => {
    inputStep.trigger?.type === ApiModel.TriggerComputedField
      ? onClickRollout(
          100,
          '',
          environments.map((env) => env.id),
        )
      : openPublishWorkflowModal();
    hideValidationErrors();
  }, [
    hideValidationErrors,
    openPublishWorkflowModal,
    onClickRollout,
    environments,
    inputStep.trigger?.type,
  ]);

  const handleClickWorkflowValidationError = useCallback(
    (errorIndex: number) => {
      setFocusedValidationErrorIndex(errorIndex);
      showValidationErrors();
    },
    [setFocusedValidationErrorIndex, showValidationErrors],
  );

  const handlePauseWorkflow = useCallback(() => {
    pauseWorkflow(workflow);
  }, [pauseWorkflow, workflow]);

  const handleActivateWorkflow = useCallback(() => {
    runWorkflow(workflow);
  }, [runWorkflow, workflow]);

  const handleConfirmRollout = (
    newRolloutPercentage: number,
    environmentIds: string[],
    description: string,
  ) => {
    closePublishWorkflowModal();
    onClickRollout(newRolloutPercentage, description, environmentIds);
  };

  const routeToWorkflowRunsPage = useCallback(() => {
    navigate(`/dashboard/${basePathRoot}/${workflowId}/runs`);
  }, [navigate, workflowId, basePathRoot]);

  const isVersionArchived = workflowVersionStatus === WorkflowVersionStatus.Archived;
  const isLatestDraft = workflowVersionId === latestDraftVersion.data?.id;
  const hasLatestDraft = latestDraftVersion.data?.id != null;
  const hasLatestPublishedVersion = latestPublishedVersion.data?.id != null;
  const isLatestPublishedVersion = workflowVersionId === latestPublishedVersion.data?.id;
  const isInRollout = workflowDeployment != null && initialRolloutPercentage < 100;
  const isNextVersionInRollout = isInRollout && workflowVersionId === treatmentVersionId;
  const workflowVersionDeploymentStatus = (() => {
    if (workflowStatus !== WorkflowStatus.Running) {
      return;
    } else if (isNextVersionInRollout) {
      return WorkflowVersionDeploymentStatus.Treatment;
    } else if (isInRollout && workflowVersionId === controlVersionId) {
      return WorkflowVersionDeploymentStatus.Control;
    } else {
      return;
    }
  })();

  const handleReviewWorkflowChanges = useCallback(() => {
    if (validationErrors.length > 0) {
      showMessage('Please fix errors before publishing', MessageType.Notice);
      showValidationErrors();
      return;
    }
    if (!isLatestPublishedVersion && hasLatestPublishedVersion && isPersonerd) {
      loadLatestPublishedVersion();
      openWorkflowVersionDiffModal();
    } else {
      handlePublishWorkflowVersion();
    }
  }, [
    validationErrors,
    showMessage,
    showValidationErrors,
    isLatestPublishedVersion,
    loadLatestPublishedVersion,
    openWorkflowVersionDiffModal,
    hasLatestPublishedVersion,
    handlePublishWorkflowVersion,
    isPersonerd,
  ]);

  const hasPermission = usePermission(Permission.WorkflowWrite);

  /**
   * WorkflowVersions are only editable if
   * * They have a latest draft and the current version is the latest draft
   * * They don't have a latest draft and the current version is latest published
   *
   * all other versions are not editable
   */
  const isEditableVersion =
    (hasLatestDraft && isLatestDraft) || (!hasLatestDraft && isLatestPublishedVersion);

  const editable =
    isEditableVersion &&
    !isShowingWorkflowVersions &&
    (workflowDeployment == null || initialRolloutPercentage === 100);

  const isShowingHeaderStatus = isInRollout || isVersionArchived || !isEditableVersion;
  const latestVersionId = hasLatestDraft
    ? latestDraftVersion.data?.id
    : latestPublishedVersion.data?.id;
  const latestEditableVersionUrl = isTriggerCase
    ? `/dashboard/case-actions/${workflowId}/${latestVersionId}`
    : `/dashboard/workflows/${workflowId}?version=${latestVersionId}`;

  // keydown shortcuts
  const keydownShortcutsEnabled = hasPermission && editable && isDirty && !loading;

  useKeydownSaveListener((e) => {
    if (keydownShortcutsEnabled) {
      e.preventDefault();
      return handleSaveWorkflowVersion();
    }
  });

  useKeydownUndoListener((e) => {
    if (keydownShortcutsEnabled && canUndo) {
      e.preventDefault();
      return undoWorkflow();
    }
  });

  const updateVersionConfig = useCallback(
    (versionAttributes: Partial<WorkflowVersionAttributes>) => {
      setWorkflowVersion({
        ...workflowVersion,
        attributes: {
          ...workflowVersion.attributes,
          ...versionAttributes,
        },
      });
    },
    [workflowVersion, setWorkflowVersion],
  );

  const onClickEditableVersionLink = useCallback(
    () => loadWorkflowVersion(latestVersionId),
    [loadWorkflowVersion, latestVersionId],
  );

  const [
    showDiscardDraftModal,
    { prompt: discardPrompt, confirm: confirmDiscardDraft, cancel: cancelDiscardDraft },
  ] = useConfirmation();

  const onClickDiscard = () => discardPrompt(() => handleDiscardDraft());

  const inactive = workflowStatus === WorkflowStatus.Paused;
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<Set<string>>(
    new Set(activeEnvironmentIds),
  );
  const [rolloutPercentage, setRolloutPercentage] = useState<number>(initialRolloutPercentage);
  const isEnvironmentSectionPristine = useMemo(
    () =>
      activeEnvironmentIds.length === selectedEnvironmentIds.size &&
      activeEnvironmentIds.every((id) => selectedEnvironmentIds.has(id)) &&
      rolloutPercentage === initialRolloutPercentage,
    [activeEnvironmentIds, selectedEnvironmentIds, rolloutPercentage, initialRolloutPercentage],
  );
  const handleSelection = useCallback(
    (value: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const newSelected = new Set<string>(selectedEnvironmentIds);
      if (e.target.checked) {
        newSelected.add(value);
      } else {
        newSelected.delete(value);
      }
      setSelectedEnvironmentIds(newSelected);
    },
    [selectedEnvironmentIds],
  );
  const handleSelectionAll = useCallback(() => {
    setSelectedEnvironmentIds(new Set(environments.map((env) => env.id)));
  }, [environments]);
  const environmentErrors = useMemo(
    () => ({
      ...(selectedEnvironmentIds.size === 0 && {
        environmentSelected: 'Please select at least 1 environment.',
      }),
    }),
    [selectedEnvironmentIds],
  );

  const environmentsNavItem: EditorNavItemLinkProps = useMemo(
    () => ({
      id: 'environments',
      label: 'Environments',
      route: {
        pathname: '.',
        search: preserveQueries({ version: workflowVersionId, config: 'environments' }),
      },
    }),
    [preserveQueries, workflowVersionId],
  );

  const configSections: ModalSectionView[] = useMemo(
    () => [
      {
        title: environmentsNavItem.label,
        navItem: environmentsNavItem,
        configParam: 'environments',
        dataTest: 'workflows-config-modal-nav__environments',
        contents: (
          <WorkflowEnvironmentsConfiguration
            editable={hasPermission}
            activeEnvironmentIds={inactive ? [] : activeEnvironmentIds}
            availableEnvironments={environments}
            selected={selectedEnvironmentIds}
            rolloutPercentage={rolloutPercentage}
            disableSlider={initialRolloutPercentage === 100 || inactive}
            canToggleRollout={false}
            errors={environmentErrors}
            setRolloutPercentage={setRolloutPercentage}
            onSelect={handleSelection}
            onSelectAll={handleSelectionAll}
          />
        ),
        onSubmit: () =>
          initialRolloutPercentage < 100
            ? onClickRollout(rolloutPercentage, undefined, Array.from(selectedEnvironmentIds))
            : onConfigureEnvironments(Array.from(selectedEnvironmentIds), rolloutPercentage),
        isSubmitting: configureEnvironmentsLoading,
        disableSubmit:
          isEnvironmentSectionPristine || !!environmentErrors.environmentSelected || inactive,
        submitButtonText:
          initialRolloutPercentage < 100
            ? rolloutPercentage === 0
              ? 'Roll back'
              : 'Roll out'
            : 'Save',
        footerText: inactive
          ? `Workflow must be active to configure environments and roll-out settings.
      Publish, roll out, or re-activate the workflow to start managing environments.`
          : rolloutPercentage === 0
            ? `Revert to previous published version and archive the current version.
      If there is no previous published version, a new draft will be created off the current version.`
            : undefined,
      },
    ],
    [
      activeEnvironmentIds,
      inactive,
      hasPermission,
      selectedEnvironmentIds,
      environments,
      rolloutPercentage,
      initialRolloutPercentage,
      environmentErrors,
      handleSelection,
      handleSelectionAll,
      configureEnvironmentsLoading,
      isEnvironmentSectionPristine,
      environmentsNavItem,
      onClickRollout,
      onConfigureEnvironments,
    ],
  );

  const diffConfigs = useMemo(() => {
    if (!publishedWorkflowVersion) {
      return [];
    }

    return [
      {
        comparedObject: {
          'steps-config': publishedWorkflowVersion.attributes['steps-config'],
        },
        currentObject: { 'steps-config': workflowVersion.attributes['steps-config'] },
        description: 'Step configurations',
      },
    ];
  }, [publishedWorkflowVersion, workflowVersion]);

  return (
    <>
      <Helmet
        title={isTriggerCase ? `${workflowName} - Actions - Cases` : `${workflowName} - Workflows`}
      />
      <ModalWrapper isOpen={showArchiveWorkflowModal} handleCloseModal={closeArchiveWorkflowModal}>
        <ConfirmationModal
          cancelAction={closeArchiveWorkflowModal}
          confirmAction={onClickArchive}
          header='Archive this workflow?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. Please ensure that this workflow is not in
            use before continuing.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper
        contentLabel='Publish workflow'
        isOpen={
          showPublishWorkflowModal ||
          showDeactivateWorkflowModal ||
          showActivateWorkflowModal ||
          showManualTriggerModal ||
          showWorkflowVersionDiffModal
        }
        handleCloseModal={() => {
          closePublishWorkflowModal();
          closeDeactivateWorkflowModal();
          closeActivateWorkflowModal();
          closeManualTriggerModal();
          closeWorkflowVersionDiffModal();
        }}
      >
        {showWorkflowVersionDiffModal && (
          <WorkflowVersionDiffModal
            diffConfigs={diffConfigs}
            baseVersionId={publishedWorkflowVersion?.id}
            targetVersionId={workflowVersion.id}
            loading={loading}
            handleClose={closeWorkflowVersionDiffModal}
            handlePublish={handlePublishWorkflowVersion}
          />
        )}
        {showPublishWorkflowModal && !isTriggerCase && (
          <WorkflowPublishModal
            activeEnvironmentIds={activeEnvironmentIds}
            availableEnvironments={environments}
            description={versionDescription}
            initialPercentage={
              workflowDeployment == null || rolloutPercentage === 100 ? 50 : rolloutPercentage
            }
            isInRollout={isInRollout}
            handleClose={closePublishWorkflowModal}
            handleConfirm={handleConfirmRollout}
            inactive={inactive}
            workflowName={workflowName}
          />
        )}
        {/* There is no staged / environment-scoped rollout for case actions
            so we show a simple confirmation modal to publish actions
            we can remove it when we GA the diff view for case actions
        */}
        {showPublishWorkflowModal && isTriggerCase && (
          <ConfirmationModal
            cancelAction={closePublishWorkflowModal}
            confirmAction={() =>
              handleConfirmRollout(
                100,
                environments.map((env) => env.id),
                '',
              )
            }
            cancelActionText='Cancel'
            confirmActionText='Confirm'
            header='Publish changes?'
            confirmButtonType='primary'
          >
            Publish a case action to activate it for your organization.
          </ConfirmationModal>
        )}
        {showDeactivateWorkflowModal && (
          <WorkflowDeactivateModal
            workflowName={workflowName}
            handleClose={closeDeactivateWorkflowModal}
            handleConfirm={handlePauseWorkflow}
          />
        )}
        {showActivateWorkflowModal && (
          <WorkflowActivateModal
            workflowName={workflowName}
            handleClose={closeActivateWorkflowModal}
            handleConfirm={handleActivateWorkflow}
            activeEnvironments={environments.filter((env) => activeEnvironmentIds.includes(env.id))}
          />
        )}
        {showManualTriggerModal && (
          <WorkflowManualRunModal
            clearResults={clearManualRunResults}
            onClose={closeManualTriggerModal}
            onDone={routeToWorkflowRunsPage}
            onSubmit={createBulkManualRuns}
            results={manualRunResults}
            showMessage={showMessage}
            isLoading={isLoadingCreateBulkManualRuns}
          />
        )}
      </ModalWrapper>
      <ModalWrapper isOpen={showDiscardDraftModal} handleCloseModal={cancelDiscardDraft}>
        <ConfirmationModal
          cancelAction={cancelDiscardDraft}
          confirmAction={confirmDiscardDraft}
          header='Discard draft?'
          confirmActionText='Discard'
        >
          All unpublished changes in this draft will be discarded. This action is permanent and
          cannot be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper
        contentLabel='Workflow configuration'
        isOpen={!!configQueryParam}
        handleCloseModal={handleCloseModal}
      >
        <WorkflowConfigModal
          versionId={workflowVersionId}
          handleCloseModal={handleCloseModal}
          onUpdateSettings={onUpdateWorkflow}
          name={workflowName}
          description={workflowDescription}
          sectionViews={configSections}
          configQueryParam={configQueryParam}
        />
      </ModalWrapper>
      {isShowingHeaderStatus && (
        <WorkflowStatusBanner
          isVersionArchived={isVersionArchived}
          isInRollout={isInRollout}
          latestEditableVersionUrl={latestEditableVersionUrl}
          isShowingWorkflowVersions={isShowingWorkflowVersions}
          showWorkflowVersions={showWorkflowVersions}
          versionStatus={workflowVersion.attributes.status}
          workflowName={workflowName}
          onClickEditableVersionLink={onClickEditableVersionLink}
        />
      )}
      <WorkflowHeader
        hasWritePermission={hasPermission}
        editable={editable}
        loading={loading}
        isDirty={isDirty}
        isCurrentPublishedVersion={isLatestPublishedVersion}
        hasLatestPublishedVersion={hasLatestPublishedVersion}
        isNextVersionInRollout={isNextVersionInRollout}
        basePathRoot={
          inputStep.trigger?.type === ApiModel.TriggerCase ? 'case-actions' : 'workflows'
        }
        parentId={workflowId}
        versionId={workflowVersionId}
        inputStep={inputStep}
        parentName={workflowName}
        isParentActiveInEnvironment={workflow.attributes.active}
        isParentActive={workflowStatus === WorkflowStatus.Running}
        isParentArchived={workflowStatus === WorkflowStatus.Archived}
        isParentInactive={inactive}
        versionStatus={workflowVersionStatus}
        workflowVersionDeploymentStatus={workflowVersionDeploymentStatus}
        rolloutPercentage={rolloutPercentage}
        onUpdateWorkflow={onUpdateWorkflow}
        onClickError={handleClickWorkflowValidationError}
        onClickSave={handleSaveWorkflowVersion}
        onClickDiscard={onClickDiscard}
        openArchiveWorkflowModal={openArchiveWorkflowModal}
        openPublishWorkflowModal={handleReviewWorkflowChanges}
        openDeactivateWorkflowModal={openDeactivateWorkflowModal}
        openActivateWorkflowModal={openActivateWorkflowModal}
        openManualTriggerModal={openManualTriggerModal}
        undoWorkflow={undoWorkflow}
        isVersionActivatable={
          stepsConfig != null && stepsConfig.length !== 0 && hasLatestPublishedVersion
        }
        canUndo={canUndo}
        createSimulatedWorkflowRun={() =>
          createSimulatedWorkflowRun(workflow.id, workflowVersionId, navigate)
        }
        showWorkflowVersions={showWorkflowVersions}
        closeWorkflowVersionView={closeWorkflowVersionView}
        isShowingVersionHistory={isShowingWorkflowVersions}
        organization={organization}
        hasStepsConfig={stepsConfig != null && stepsConfig.length > 0}
        validationErrors={validationErrors}
        currentEnvironmentType={currentEnvironmentType}
      />
      <LoadingModal isLoading={loading} />
      <WorkflowEditor
        workflowSchema={workflowSchema}
        transactionTypes={transactionTypes}
        versionConfig={pick(
          ['description', 'outputs', 'steps-config', 'time-out-minutes'],
          workflowVersion.attributes,
        )}
        latestDraftVersionId={workflow.relationships['latest-draft-version'].data?.id}
        versionId={workflowVersion.id}
        parentId={workflow.id}
        parentName={workflow.attributes.name}
        inputStep={inputStep}
        updateVersionConfig={updateVersionConfig}
        setInputStep={setInputStep}
        editable={editable && hasPermission}
        workflowDeployment={workflowDeployment ?? undefined}
        validationErrors={visibleValidationErrors ? validationErrors : []}
        isShowingHeaderStatus={isShowingHeaderStatus}
        isShowingWorkflowVersions={isShowingWorkflowVersions}
        isLoadingVersionList={isLoadingVersionList}
        closeWorkflowVersionView={closeWorkflowVersionView}
        isDirty={isDirty}
        isCurrentPublishedVersion={isLatestPublishedVersion}
        focusedValidationErrorIndex={focusedValidationErrorIndex ?? undefined}
        canAddModule={!isTriggerCase}
        versionsList={versionsList}
        clearFocusedValidationErrorIndex={() => setFocusedValidationErrorIndex(null)}
        loadWorkflowVersion={loadWorkflowVersion}
        loadFilterFieldConfigs={loadFilterFieldConfigs}
        loadVersionList={loadVersionList}
        restoreDraftVersion={restoreDraftVersion}
        handleDiscardDraft={handleDiscardDraft}
        fetchPlatformObjectJsonSchema={fetchPlatformObjectJsonSchema}
      />
    </>
  );
};

export default DashboardWorkflowsShowView;
